import { useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { EntryLayout } from '../components/EntryLayout';
import { Icon } from '../components/Icon';
import { portalApi } from '../api/client';

const STEPS = [
  'Enter your email to receive a secure access link',
  'Click the link to sign in. No password required.',
  'Submit and manage your ideas from your dashboard.',
];

/**
 * Magic-link request page (`/`) — split-screen layout (US3).
 *
 * Always shows the same generic confirmation on submit regardless of whether
 * the address is registered (no enumeration, AC3.1). Also serves as the
 * landing target for the US6 session-expiry redirect: `?expired=1` shows the
 * "session expired" banner.
 */
export function RequestLinkPage() {
  const [params] = useSearchParams();
  const expired = params.get('expired') === '1';
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    try {
      // Generic response either way — ignore the body, never branch on it.
      await portalApi.requestLink(email).catch(() => undefined);
    } finally {
      setSubmitting(false);
      setSent(true);
    }
  }

  if (sent) {
    return (
      <EntryLayout
        tagline="Check your inbox"
        description="A secure access link is on its way. Links are single-use and expire in 1 hour."
      >
        <div className="rounded-card border-[1.5px] border-success-mid bg-success-light p-7 text-center">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-[#D1FAE5] text-success">
            <Icon name="mail" size={22} />
          </div>
          <div className="mb-1 font-bold text-success">Link sent</div>
          <div className="text-sm text-muted">
            If your address is registered you'll receive an access link. Check spam if it
            doesn't arrive.
          </div>
        </div>
        <p className="mt-5 text-center text-sm text-muted">
          Wrong address?{' '}
          <button
            type="button"
            className="font-semibold text-accent"
            onClick={() => setSent(false)}
          >
            Go back
          </button>
        </p>
      </EntryLayout>
    );
  }

  return (
    <EntryLayout
      tagline="Turn your ideas into real value"
      description="Submit use-case ideas for your organisation. No Vane Loop account needed."
      steps={STEPS}
    >
      {expired && (
        <div
          role="alert"
          className="mb-5 rounded-card border-[1.5px] border-[#FECACA] bg-[#FEF2F2] p-4 text-center"
        >
          <div className="mb-1 flex items-center justify-center gap-2 font-bold text-danger">
            <Icon name="clock" size={16} /> Your session expired
          </div>
          <div className="text-sm text-muted">
            Sessions last 1 hour. Your ideas are saved. Request a new link to continue.
          </div>
        </div>
      )}
      <h2 className="mb-1 text-2xl font-extrabold tracking-tight">Request access</h2>
      <p className="mb-7 text-sm leading-snug text-muted">
        Enter your work email. If your address is registered, we'll send you an access link.
      </p>
      <form onSubmit={handleSubmit}>
        <div className="mb-4">
          <label
            htmlFor="email"
            className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-muted"
          >
            Email address
          </label>
          <input
            id="email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@company.com"
            className="w-full rounded-[10px] border-[1.5px] border-line px-3.5 py-3 text-sm text-ink outline-none focus:border-accent"
          />
        </div>
        <button
          type="submit"
          disabled={submitting}
          className="w-full rounded-[10px] bg-accent py-3 text-sm font-bold text-white transition-colors hover:bg-accent-hover disabled:opacity-60"
        >
          {submitting ? 'Sending…' : 'Send access link'}
        </button>
      </form>
      <p className="mt-3 text-center text-xs leading-snug text-muted">
        Only pre-approved addresses can access the portal. Contact your Vane Loop admin for
        access.
      </p>
    </EntryLayout>
  );
}
